import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// Los motivos que se repiten. Son atajos, no una lista cerrada: siempre se puede
// escribir otro, porque el que no está en la lista es justo el que más enseña.
const COMMON_REASONS = [
  "Precio",
  "No llegamos al plazo",
  "No cumplíamos los requisitos",
  "Ganó otro proveedor",
  "La entidad la declaró desierta",
];

/**
 * Por qué se perdió. Se pregunta al mover a PERDIDA porque después nadie vuelve a
 * llenarlo, y sin motivo el histórico de perdidas no responde ninguna pregunta.
 */
export function LostReasonDialog({
  open,
  label,
  isSaving,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  label: string;
  isSaving: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");

  function close() {
    setReason("");
    onCancel();
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>¿Por qué se perdió?</DialogTitle>
          <DialogDescription className="line-clamp-2">{label}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {COMMON_REASONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setReason(option)}
              className={cn(
                "cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                reason === option
                  ? "border-primary bg-primary/10 text-primary"
                  : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800",
              )}
            >
              {option}
            </button>
          ))}
        </div>

        <textarea
          id="lost-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Escribe el motivo con tus palabras..."
          className="w-full rounded-xl border bg-slate-50/50 p-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none dark:bg-slate-900/10 dark:text-slate-200"
        />

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={close} className="cursor-pointer">
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onConfirm(reason.trim());
              setReason("");
            }}
            disabled={isSaving || !reason.trim()}
            className="cursor-pointer"
          >
            {isSaving ? "Guardando..." : "Marcar como perdida"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
